import { Component, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { AppraisalFilesService } from '../services/appraisal-files.service';

type UploadStatus = 'PENDIENTE' | 'PROCESANDO' | 'ENVIADO' | 'ERROR';

interface CsvRow {
  ID: string;
  N_EXPEDIENTE: string;
  NOMBRE: string;
  FECHA_EMISION: string;
  ESTADO?: UploadStatus;
  ERROR?: string;
}

@Component({
  selector: 'app-massive-upload',
  imports: [CommonModule],
  templateUrl: './massive-upload.html',
  styleUrl: './massive-upload.css',
})
export class MassiveUpload {
  directoryPath = signal<string>('');
  csvFilePath = signal<string>('');
  errorMessage = signal<string>('');
  csvData = signal<CsvRow[]>([]);
  displayedRows = signal<CsvRow[]>([]);
  isValidCsv = signal<boolean>(false);
  isUploading = signal<boolean>(false);
  uploadFinished = signal<boolean>(false);
  selectedFiles: File[] = [];

  private readonly REQUIRED_HEADERS = ['ID', 'N_EXPEDIENTE', 'NOMBRE', 'FECHA_EMISION'];
  private readonly ROWS_PER_PAGE = 10;
  private stopUploadRequested = false;

  private authService = inject(AuthService);
  private appraisalFilesService = inject(AppraisalFilesService);

  constructor(private router: Router) {}

  goBack() {
    this.router.navigate(['/home']);
  }

  selectDirectory() {
    // Prevent folder selection during upload
    if (this.isUploading()) {
      return;
    }
    
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('webkitdirectory', '');
    input.setAttribute('directory', '');
    
    input.onchange = (event: any) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        this.selectedFiles = Array.from(files);
        // Get the path from the first file
        const firstFile = files[0];
        const path = firstFile.webkitRelativePath || firstFile.name;
        const directoryName = path.split('/')[0];
        this.directoryPath.set(directoryName);
        
        // Reset upload state when new folder is selected
        this.uploadFinished.set(false);
      }
    };
    
    input.click();
  }

  selectCsvFile() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', '.csv');
    
    input.onchange = (event: any) => {
      const file = event.target.files?.[0];
      if (file) {
        this.csvFilePath.set(file.name);
        this.processCSVFile(file);
      }
    };
    
    input.click();
  }

  private processCSVFile(file: File) {
    const reader = new FileReader();
    
    reader.onload = (e: any) => {
      try {
        const content = e.target.result;
        this.validateAndParseCSV(content);
      } catch (error) {
        this.errorMessage.set('Error al leer el archivo CSV');
        this.isValidCsv.set(false);
      }
    };
    
    reader.readAsText(file);
  }

  private validateAndParseCSV(content: string) {
    this.errorMessage.set('');
    this.csvData.set([]);
    this.displayedRows.set([]);
    this.isValidCsv.set(false);

    const lines = content.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    
    if (lines.length < 2) {
      this.errorMessage.set('El archivo CSV debe contener al menos una fila de encabezados y una fila de datos');
      return;
    }

    // Validate headers
    const headers = lines[0].split(';').map(h => h.trim());
    
    if (headers.length !== this.REQUIRED_HEADERS.length) {
      this.errorMessage.set(`El CSV debe tener exactamente ${this.REQUIRED_HEADERS.length} columnas: ${this.REQUIRED_HEADERS.join(', ')}`);
      return;
    }

    for (let i = 0; i < this.REQUIRED_HEADERS.length; i++) {
      if (headers[i] !== this.REQUIRED_HEADERS[i]) {
        this.errorMessage.set(`La columna ${i + 1} debe ser "${this.REQUIRED_HEADERS[i]}" pero se encontró "${headers[i]}"`);
        return;
      }
    }

    // Validate and parse data rows
    const data: CsvRow[] = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(';').map(v => v.trim());
      
      if (values.length !== this.REQUIRED_HEADERS.length) {
        this.errorMessage.set(`La fila ${i + 1} debe tener ${this.REQUIRED_HEADERS.length} columnas, pero tiene ${values.length}`);
        return;
      }

      // Check for empty values
      for (let j = 0; j < values.length; j++) {
        if (values[j] === '') {
          this.errorMessage.set(`La fila ${i + 1}, columna "${this.REQUIRED_HEADERS[j]}" está vacía. Todos los campos son obligatorios`);
          return;
        }
      }

      data.push({
        ID: values[0],
        N_EXPEDIENTE: values[1],
        NOMBRE: values[2],
        FECHA_EMISION: values[3]
      });
    }

    // If we got here, validation passed
    this.csvData.set(data);
    this.displayedRows.set(data.slice(0, this.ROWS_PER_PAGE));
    this.isValidCsv.set(true);
  }

  downloadExampleCsv() {
    const exampleContent = `ID;N_EXPEDIENTE;NOMBRE;FECHA_EMISION
303821;ALR821;20250808_13008_3455;10/08/2025
303822;ALR822;20250808_13008_3456;11/08/2025
303823;ALR823;20250808_13008_3457;12/08/2025`;

    const blob = new Blob([exampleContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'ejemplo.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  onTableScroll(event: Event) {
    const target = event.target as HTMLElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    // Load more rows when scrolling near the bottom
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      const currentLength = this.displayedRows().length;
      const totalLength = this.csvData().length;
      
      if (currentLength < totalLength) {
        const nextRows = this.csvData().slice(0, Math.min(currentLength + this.ROWS_PER_PAGE, totalLength));
        this.displayedRows.set(nextRows);
      }
    }
  }

  async startUpload() {
    if (!this.isValidCsv() || !this.directoryPath()) {
      return;
    }

    this.isUploading.set(true);
    this.uploadFinished.set(false);
    this.stopUploadRequested = false;

    // Initialize all rows with PENDIENTE status
    const rows = this.csvData().map(row => ({ ...row, ESTADO: 'PENDIENTE' as UploadStatus }));
    this.csvData.set(rows);
    this.updateDisplayedRows();

    // Process each row
    for (let i = 0; i < rows.length; i++) {
      if (this.stopUploadRequested) {
        break;
      }

      await this.processRow(i);
    }

    this.isUploading.set(false);
    this.uploadFinished.set(true);
  }

  stopUpload() {
    this.stopUploadRequested = true;
  }

  private async processRow(index: number) {
    const rows = [...this.csvData()];
    const row = rows[index];

    // Set status to PROCESANDO
    row.ESTADO = 'PROCESANDO';
    this.csvData.set(rows);
    this.updateDisplayedRows();

    try {
      // Find the file
      const file = await this.findFile(row.NOMBRE);
      
      if (!file) {
        throw new Error(`Archivo no encontrado: ${row.NOMBRE} o ${row.NOMBRE}.pdf`);
      }

      // Upload the file
      await this.uploadFile(file, row);

      // Set status to ENVIADO
      row.ESTADO = 'ENVIADO';
      delete row.ERROR;
    } catch (error: any) {
      // Set status to ERROR
      row.ESTADO = 'ERROR';
      row.ERROR = error.message || 'Error desconocido al subir el archivo';
    }

    this.csvData.set(rows);
    this.updateDisplayedRows();
  }

  private async findFile(fileName: string): Promise<File | null> {
    // Try to find the file with the exact name
    let file = this.selectedFiles.find(f => {
      const name = f.webkitRelativePath?.split('/').pop() || f.name;
      return name === fileName;
    });

    // If not found, try with .pdf extension
    if (!file) {
      file = this.selectedFiles.find(f => {
        const name = f.webkitRelativePath?.split('/').pop() || f.name;
        return name === `${fileName}.pdf`;
      });
    }

    return file || null;
  }

  private async uploadFile(file: File, row: CsvRow): Promise<void> {
    // Parse and validate date from DD/MM/YYYY format
    const dateParts = row.FECHA_EMISION.split('/');
    if (dateParts.length !== 3) {
      throw new Error(`Formato de fecha inválido: ${row.FECHA_EMISION}. Debe ser DD/MM/YYYY`);
    }
    
    const [day, month, year] = dateParts;
    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);
    
    if (isNaN(dayNum) || isNaN(monthNum) || isNaN(yearNum)) {
      throw new Error(`Fecha inválida: ${row.FECHA_EMISION}. Debe contener números válidos`);
    }
    
    const documentDate = new Date(`${year}-${month}-${day}`);
    if (isNaN(documentDate.getTime())) {
      throw new Error(`Fecha inválida: ${row.FECHA_EMISION}`);
    }

    try {
      await firstValueFrom(
        this.appraisalFilesService.uploadFile({
          file: file,
          appraisalId: row.ID,
          documentObtainedDate: documentDate.toISOString()
        })
      );
    } catch (error: any) {
      const errorMessage = error.error?.message || error.message || 'Error al subir el archivo';
      throw new Error(`Error en la subida: ${errorMessage}`);
    }
  }

  private updateDisplayedRows() {
    const currentLength = this.displayedRows().length;
    this.displayedRows.set(this.csvData().slice(0, Math.max(currentLength, this.ROWS_PER_PAGE)));
  }

  downloadErrorsCsv() {
    const errorRows = this.csvData().filter(row => row.ESTADO === 'ERROR');
    
    if (errorRows.length === 0) {
      return;
    }

    const headers = [...this.REQUIRED_HEADERS, 'ERROR'];
    const csvContent = [
      headers.join(';'),
      ...errorRows.map(row => 
        `${row.ID};${row.N_EXPEDIENTE};${row.NOMBRE};${row.FECHA_EMISION};${row.ERROR || ''}`
      )
    ].join('\n');

    this.downloadCsv(csvContent, 'errores.csv');
  }

  downloadSuccessCsv() {
    const successRows = this.csvData().filter(row => row.ESTADO === 'ENVIADO');
    
    if (successRows.length === 0) {
      return;
    }

    const csvContent = [
      this.REQUIRED_HEADERS.join(';'),
      ...successRows.map(row => 
        `${row.ID};${row.N_EXPEDIENTE};${row.NOMBRE};${row.FECHA_EMISION}`
      )
    ].join('\n');

    this.downloadCsv(csvContent, 'exitosos.csv');
  }

  private downloadCsv(content: string, filename: string) {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  canStartUpload(): boolean {
    return this.isValidCsv() && this.directoryPath() !== '' && !this.isUploading();
  }
}
